import { ExerciseRow } from "@/book/components/rows";
import {
  useChapters,
  useExercises,
  type Attitude,
  type ExerciseQuery,
} from "@/shared/api/book";
import { ATTITUDES, ATTITUDE_LABEL, chapterOptions, MARK_LABEL } from "@/shared/lib/labels";
import { num, useFilters } from "@/shared/lib/use-filters";
import { FilterRow, FilterSelect, SearchInput } from "@/shared/ui/filters";
import { PageTitle } from "@/shared/ui/page-header";
import { Empty, ErrorNote, ListSkeleton, Pager } from "@/shared/ui/state";

const DEFAULTS = { q: "", chapter_id: "", attitude: "", mark: "", page: "1" };

/** Saját jelölések: „Még nem jelöltem" (nincs állapota — a kedvenc és a jegyzet ettől
 *  független), a gyakorlat-oldali állapotok, a kedvenc és a jegyzet. */
const MARKS = [
  { value: "none", label: "Még nem jelöltem" },
  { value: "want_to_try", label: MARK_LABEL.want_to_try },
  { value: "tried", label: MARK_LABEL.tried },
  { value: "not_for_me", label: MARK_LABEL.not_for_me },
  { value: "favorite", label: "Kedvenc" },
  { value: "noted", label: "Van jegyzetem" },
];

export function ExercisesPage() {
  const { values, set } = useFilters(DEFAULTS);
  const { data: chapters } = useChapters();

  const query: ExerciseQuery = {
    q: values.q,
    chapter_id: num(values.chapter_id),
    attitude: values.attitude as Attitude | "",
    mark: values.mark as ExerciseQuery["mark"],
    page: Number(values.page) || 1,
  };
  const { data, isPending, isError } = useExercises(query);
  const filtered = Object.entries(values).some(([k, v]) => k !== "page" && Boolean(v));

  return (
    <div className="flex flex-col gap-4">
      <PageTitle>Gyakorlattár</PageTitle>

      <SearchInput
        label="Keresés a gyakorlatok között"
        placeholder="Keresés a gyakorlatok között"
        value={values.q}
        onChange={(q) => set({ q })}
      />

      <FilterRow>
        {chapters && chapters.length > 0 && (
          <FilterSelect
            label="Fejezet"
            allLabel="Minden fejezet"
            options={chapterOptions(chapters)}
            value={values.chapter_id}
            onChange={(chapter_id) => set({ chapter_id })}
          />
        )}
        <FilterSelect
          label="Hozzáállás"
          allLabel="Minden hozzáállás"
          options={ATTITUDES.map((a) => ({ value: a, label: ATTITUDE_LABEL[a] }))}
          value={values.attitude}
          onChange={(attitude) => set({ attitude })}
        />
        <FilterSelect
          label="Saját jelöléseim"
          allLabel="Minden jelölés"
          options={MARKS}
          value={values.mark}
          onChange={(mark) => set({ mark })}
        />
      </FilterRow>

      {isPending && <ListSkeleton />}
      {isError && <ErrorNote />}
      {data && (
        <>
          <p className="text-[12px] text-muted">{data.total} gyakorlat</p>
          {data.items.length === 0 ? (
            <Empty>
              {filtered
                ? "Erre a szűrésre nincs találat."
                : "A gyakorlatok még nincsenek feltöltve."}
            </Empty>
          ) : (
            <div className="flex flex-col">
              {data.items.map((e) => (
                <ExerciseRow key={e.id} exercise={e} />
              ))}
            </div>
          )}
          <Pager
            page={data.page}
            total={data.total}
            pageSize={data.page_size}
            onChange={(page) => set({ page: String(page) })}
          />
        </>
      )}
    </div>
  );
}
